/*
 * -----------------------------------------------------------------------
 *  <copyright file="import-library-risks.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { Country, getData } from 'country-list';
import { NamedItemFields as NamedItemFieldsWithAudit } from 'queries/generated/fragments.generated';
import { NamedItemType, TimeFrequencyType } from 'types/graphql-global-types';
import {
    checkData,
    checkLine,
    readBool,
    readEnum,
    readLov,
    readNumber,
    readString,
    splitString,
} from 'utils/importers/helpers';
import doImport from 'utils/importers/import-file';

const importLibraryRisks = (
    data: any,
    lovs: Exclude<NamedItemFieldsWithAudit, 'auditId'>[],
    isSystemLevel: boolean,
) => {
    const risks: any = [];
    const errors: any = [];

    if (!checkData(data, errors)) return { data: [], errors };

    data.forEach((line: any, index: number) => {
        if (!checkLine(line, index, errors)) return;

        const initialErrorCount = errors.length;
        const statement = readString('Statement', line, 0, index + 1, errors, 600, true);
        if (!statement) return;

        if (errors.length !== initialErrorCount) return;
        // System level risks only require a Statement
        if (isSystemLevel) {
            risks.push({
                id: null as any,
                active: true,
                statement,
            });
            return;
        }

        const countryList = Object.assign(
            [],
            getData().map((countryObject: { name: string; code: string }) => ({
                name: countryObject.name,
                code: countryObject.code,
            })),
        );

        const responsibleFunctionId = readLov(
            'Responsible Function',
            lovs,
            NamedItemType.RESPONSIBLE_FUNCTION,
            line,
            1,
            index + 1,
            errors,
        );
        const controlStrategyId = readLov(
            'Control Strategy',
            lovs,
            NamedItemType.RISK_CONTROL_STRATEGY,
            line,
            2,
            index + 1,
            errors,
        );
        const riskCategoryId = readLov('Category', lovs, NamedItemType.RISK_CATEGORY, line, 3, index + 1, errors);
        let riskLibraryCategoryName: string[] = [];
        const riskLibraryCategoryIds: any[] = [];

        riskLibraryCategoryName = splitString('Library Category', line, 4, index + 1, errors, ',');
        for (let i: number = 0; i < riskLibraryCategoryName?.length; i++) {
            const id = readLov(
                'Library Category',
                lovs,
                NamedItemType.RISK_LIBRARY_CATEGORY,
                riskLibraryCategoryName,
                i,
                index + 1,
                errors,
            );
            if (id != null && !riskLibraryCategoryIds.includes(id)) {
                riskLibraryCategoryIds.push(id);
            }
        }

        const errorsBeforeReviewFrequency = errors.length;

        let reviewRequired: boolean | null = null;
        reviewRequired = readBool('Review Required', line, 5, index + 1, errors, true);

        let reviewFrequencyPeriod = !reviewRequired
            ? null
            : readNumber('Review Frequency Unit', line, 6, index + 1, errors);

        if (
            reviewFrequencyPeriod !== null &&
            !isNaN(reviewFrequencyPeriod) &&
            (reviewFrequencyPeriod <= 0 || reviewFrequencyPeriod > 31)
        ) {
            errors.push({
                line: index + 1,
                message: `Invalid review frequency units (${reviewFrequencyPeriod}) - must be between 1-31.`,
            });
            reviewFrequencyPeriod = null;
        }

        let reviewFrequencyUnits = !reviewRequired
            ? null
            : readEnum(
                  'Review Frequency Period',
                  [
                      TimeFrequencyType.DAILY,
                      TimeFrequencyType.WEEKLY,
                      TimeFrequencyType.MONTHLY,
                      TimeFrequencyType.QUARTERLY,
                      TimeFrequencyType.ANNUALLY,
                  ],
                  line,
                  7,
                  index + 1,
                  errors,
              );

        if (!reviewRequired) {
            reviewFrequencyPeriod = null;
            reviewFrequencyUnits = null;
        }

        if (
            reviewRequired &&
            errors.length === errorsBeforeReviewFrequency &&
            ((reviewFrequencyPeriod !== null && !reviewFrequencyUnits) ||
                (reviewFrequencyPeriod === null && reviewFrequencyUnits))
        )
            errors.push({ line: index + 1, message: 'Both period and units must be populated.' });

        let riskLibraryVendorName: string[] = [];
        const vendorIds: any[] = [];

        riskLibraryVendorName = splitString('Vendors', line, 8, index + 1, errors, ',') ?? [];
        for (let i: number = 0; i < riskLibraryVendorName?.length; i++) {
            const id = readLov('Vendors', lovs, NamedItemType.VENDOR, riskLibraryVendorName, i, index + 1, errors);

            if (id != null && !vendorIds.includes(id)) {
                vendorIds.push(id);
            }
        }

        let riskLibraryCountryName: string[] = [];
        const countries: Country[] = [];

        riskLibraryCountryName = splitString('Country', line, 9, index + 1, errors, ',');
        riskLibraryCountryName?.forEach((name) => {
            if (name === '') return;
            const validCountry: Country | undefined = countryList.find(
                (c: Country) => c.name.toLowerCase() === name.trim().toLowerCase(),
            );

            if (validCountry) {
                if (!countries.some((c) => c.code === validCountry.code)) {
                    countries.push(validCountry);
                }
            } else {
                errors.push({ line: index + 1, message: `Country "${name}" not found.` });
            }
        });

        // If there are any errors through this, then don't add the risk
        if (errors.length !== initialErrorCount) return;

        const riskImport = {
            id: null as any,
            active: true,
            statement,
            responsibleFunctionId,
            controlStrategyId,
            riskCategoryId,
            riskLibraryCategoryIds,
            reviewRequired,
            reviewFrequencyPeriod,
            reviewFrequencyUnits,
            vendorIds,
            countries,
        };

        risks.push(riskImport);
    });
    return { data: risks, errors };
};

export default (file: any, lovs: Exclude<NamedItemFieldsWithAudit, 'auditId'>[], isSystemLevel: boolean) =>
    doImport({ file, lovs, isSystemLevel, importer: importLibraryRisks });
